import { Fragment } from "react"
import { useState, useRef } from "react"

import { gql } from "@apollo/client"
import craftApolloClient from "./api/apollo"

import Link from "next/link"

import _ from 'lodash'
import { format } from 'date-fns'
import parse from 'html-react-parser'

import { LenisScroller } from '../fuselage/animation/lenis-scroller'
import { Reveal } from '../fuselage/animation/reveal'
import { Pop, Fade, Slide } from '../fuselage/animation/animation'


import { HeaderShim } from '../fuselage/global/header/header'
import { FooterShim } from "../fuselage/global/footer/footer"

import { PageHeader } from '../fuselage/blocks/page-header/page-header'
import { ButtonOutline } from "../fuselage/components/button-outline/button-outline"

import { TagIcon, CompassIcon, StopwatchIcon, LocationIcon } from '../fuselage/components/icons/icons'

import CSS from '../css/app/agenda-2024.module.css'


export default function Agenda ({ sessions, theme }) {

    const [ selectedTopic, setSelectedTopic ] = useState (null)
    const [ selectedDay, setSelectedDay ] = useState (null)
    const [ selectedLocation, setSelectedLocation ] = useState(null)

	const locationSelect = useRef()

	

	// FILTERS

	const resetFilters = () => {
		setSelectedDay(null)
		setSelectedLocation(null)
		setSelectedTopic(null)
		locationSelect.current.value = 'All locations'
	}

	

	// BUILD AGENDA
		
	const buildAgenda = ( sessionsData ) => {

		if ( !sessionsData ) return 
		
		const timeslots = _.groupBy(sessionsData, 'beginsAt')
		

		// calculate event days

		const getUniqueDays = ( sessionData ) => {

			const groupedStartTimes = _.groupBy(sessionData, 'beginsAt')
			const timeCodes = Object.keys(groupedStartTimes)
			const eventDays = _.uniqBy( timeCodes, string => string.slice(0, 10) )
	
			return eventDays
		}
		
		const agendaDays = []
		getUniqueDays( sessionsData ).map((timestring, index) => {
			agendaDays.push({ 
				day: format( new Date(timestring), "eeee dd MMMM" ), 
				time: timestring.slice(0, 10), 
				timeslots: [] 
			})
		})
		

		// filters

		const filterTopics = ( sessionData ) => {
		
			const filteredSessions = []
			
			if ( selectedTopic ) {
				// console.log('selectedTopic', selectedTopic)
				sessionData.forEach( session => {
					if ( session.fields.some( field => field.id == selectedTopic )) filteredSessions.push(session)
				})
			}
	
			// console.log('filters (topic)', filteredSessions.length ? filteredSessions.length : sessionData.length )
			return filteredSessions.length ? filteredSessions : sessionData
			
			
		}	
		
		const filterLocations = ( sessionData ) => {
			
			const filteredSessions = []
			
			if ( selectedLocation ) {
				// console.log('selectedLocation', selectedLocation)
				sessionData.forEach( session => {
					if ( session.fields.some( field => field.id == selectedLocation )) filteredSessions.push(session)
				})
			}
	
			// console.log('locations:', filteredSessions.length ? filteredSessions.length : sessionData.length )
			return filteredSessions.length ? filteredSessions : sessionData
			
			
		}
	
		const filterSessions = ( sessionData ) => {
			// console.log('filter intersection:', _.intersection( filterLocations(sessionData), filterTopics(sessionData) ))
			return _.intersection( filterLocations(sessionData), filterTopics(sessionData) )
		}


		// apply filters to sessions

		const filteredTimeslots = _.groupBy(filterSessions(sessionsData), 'beginsAt')
		
	
		// build agenda
	
		const agenda = []
		agendaDays.map( day => {
			let slots = []
			for ( const key in filteredTimeslots ) {
				if ( key.slice(0, 10) === day.time.slice(0, 10) ) {
					slots.push({ 
						beginsAt: filteredTimeslots[key][0].beginsAt, 
						endsAt: filteredTimeslots[key][0].endsAt, 
						sessions: filteredTimeslots[key]
					})
				}
			}
	
			agenda.push( { ...day , timeslots: slots } )
		})

		// console.log('agenda:', agenda)
		return agenda
	}


	
	// HANDLE SESSION FIELDS (TAGS)

	const getSessionFields = ( sessionData, fieldHandle ) => {
		if ( !sessionData.fields || !fieldHandle ) return

		let fieldsData = sessionData.fields.filter( (field) => field.definition.name === fieldHandle )

		return fieldsData.map ( (topic, index) => (
			<span key={index} className='sessionTag fw-500'>{ topic.translations[0].value }</span> 
		))
	}

	const getSessionLocation = ( sessionData ) => {
		if ( !sessionData.fields ) return

		let fieldsData = sessionData.fields.filter( (field) => field.definition.name === 'Location' )

		return fieldsData.map ( (topic, index) => (
			<span key={index} className='sessionLocationTag fw-500'>{ topic.translations[0].value }</span> 
		))
	}

	const getSessionLocationTag = ( sessionData ) => {
		
		if ( !sessionData.fields ) return
		
		let locationObject = sessionData.fields.find( (field) => field.definition.name === 'Location' )

		if ( locationObject ) {
			let tagTheme = theme.find( obj => obj.agendaTag === locationObject.translations[0].value )			
			
			return (
				<>
					<CompassIcon fill={tagTheme.color}/>
					<span className='sessionTag fw-500' style={{ background: tagTheme.color }}>{ locationObject.translations[0].value }</span> 
				</>
			)
		}
		return
	}

	const getSessionLocationTheme = ( sessionData ) => {
		
		if ( !sessionData.fields ) return
		
		let locationObject = sessionData.fields.find( (field) => field.definition.name === 'Location' )

		if ( locationObject ) {
			let tagTheme = theme.find( obj => obj.agendaTag === locationObject.translations[0].value )			
			
			if ( theme.color) return tagTheme.color

			return
		}
		return
	}

	const groupFieldGroupsByName = ( sessionsData, fieldHandle ) => {
		if ( !sessionsData || !fieldHandle ) return

		const fieldIds = []
		const fieldObjects = []
		const distilledFieldObjects = []

		// build arrays
		sessionsData.forEach( (session) => {
			let topics = session.fields.filter( (field) => field.definition.name === fieldHandle )
			topics.forEach( (field) => {
				fieldIds.push(field.id)
				fieldObjects.push({ id: field.id, name: field.translations[0].value })
			})
		})

		// for unique IDs, search the field object array to find the related name
		_.uniq(fieldIds).forEach( (id) => {
			distilledFieldObjects.push(fieldObjects.find( (obj) => obj.id === id ))
		})

		return distilledFieldObjects
	}

	const getFieldGroupNameById = ( sessionData, fieldId ) => {
		if ( !fieldId ) return
		let array = groupFieldGroupsByName( sessionData, 'Topics' )
		let obj = array.find( topic => topic.id === fieldId)
		return obj.name
	}

	// HANDLE SPEAKERS PER SESSION (pick out moderators)

	const sortSpeakers = ( speakerData ) => {

		if ( !speakerData ) return

		// console.log('speakerData:', speakerData)

		let moderators = []
        let speakers = []

		speakerData.forEach( speaker => {
			speaker.withEvent?.eventProfile.type !== 'Speaker' ? moderators.push( speaker ) : speakers.push( speaker )
		})


		if ( moderators.length && speakers.length ) return (
			<section className="sessionSpeakers">
				{
					moderators.length ?
						<div>
							<p className="fw-500" style={{ color: `var(--secondary)`, padding: `0 0 15px 0`}}>Moderator{ moderators.length > 1 && 's'}</p>
							<div className='sessionSpeakerGrid'>
								{ moderators.map( moderator => <SpeakerCard key={moderator.id} speaker={moderator} /> ) }
							</div>
						</div>
					: null
				}
				<div>
					{
						moderators.length && speakers.length ?
							<p className="fw-500" style={{ color: `var(--secondary)`, padding: `0 0 15px 0`}}>In conversation with</p>
						: null
					}
					<div className='sessionSpeakerGrid'>
						{ speakers.map( speaker => <SpeakerCard key={speaker.id} speaker={speaker} /> ) }
					</div>
				</div>

			</section>
		)

		return (
			<div className='sessionSpeakerGrid'>
				{ speakerData.map( speaker => <SpeakerCard key={speaker.id} speaker={speaker} /> ) }
			</div>
		)
	}



	// utilities

	const breakTitle = ( trigger, titleString, splitPoint ) => {
		if ( !titleString.includes(trigger) ) return titleString
		let lines = titleString.split(splitPoint)
		return `${lines[0]}:<br/>${lines[1]}${lines.length > 2 ? `: ${lines[2]}` : ''}`		
	}
  

	// SESSION UTILITIES

	const convertSeconds = (seconds) => {
		const hours = Math.floor(seconds / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
	
		const hourString = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : ''
		const minuteString = minutes > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''}` : ''
	
		if ( hours > 0 ) {
			return `${hourString} ${ minutes > 0 ? minuteString : ''}`

		} else if ( !hours && minutes > 0 ) {
			return `${minuteString}`

		}
	
		return
	}

	const setSessionTheme = ( sessionData ) => {

		if ( !sessionData.fields ) return

		let fieldsData = sessionData.fields.filter( (field) => field.definition.name === 'Location' )

		switch (fieldsData[0]?.id) {
			case "RmllbGRWYWx1ZV8yNTkzNDU1Nw==":
				//Knowledge HUB
				return "agenda-01"
				break
		
			case "RmllbGRWYWx1ZV8yNTkzNDU2MA==":
				//The Forum
				return "agenda-02"
				break
		
			case "RmllbGRWYWx1ZV8yNTkzNDU1OQ==":
				//Pioneer HUB
				return "agenda-03"
				break
		
			case "RmllbGRWYWx1ZV8yNTkzNDU1NQ==":
				//Future HUB
				return "agenda-04"
				break
		
			case "RmllbGRWYWx1ZV8yNTkzNDU1OA==":
				//Showcase HUB
				return "agenda-05"
				break
		
			default:
				return "bg-light"
				break
		}

		return

	}


	// console.log('sessions:', sessions)
	// console.log('theme:', theme)	
	// console.log(groupFieldGroupsByName( sessions, 'Location'))


    return (
        <div style={{ width: `100%`, overflow: `hidden` }}>

            <HeaderShim />

            <PageHeader theme={``}>
                <div className="formatted no-print">
                    <p className="fs-0 fw-600"><Reveal>2025 Agenda</Reveal></p>
					<Slide>
						<p className="mt-sm">For FHS Africa, our objective is to enhance networking opportunities by introducing innovative and engaging formats that foster meaningful connections among delegates. The program is carefully designed to encourage greater interaction between attendees and speakers, ensuring valuable discussions and insights in different rooms at the Westin.</p>
						<p>Additionally, the most critical topics will be presented in a traditional conference format on Day 2 (18/02) at Boma Talks, held at the CCITC, providing a dedicated platform for in-depth discussions and thought leadership.</p>
					</Slide>
                </div>

            </PageHeader>
			<p className="block py-0 fs-0 fw-600 print-only">FHS Africa 2025 Agenda</p>

            <section className="block">
				<div className="container columns-4 gap-sm pt-md">
					<div className="formatted">
						<h5>Knowledge Station</h5>
						<p>A dedicated space for learning and inspiration, the Knowledge Station offers engaging workshops, in-depth case studies, and expert presentations. Designed to address key focus topics from the conference, this hub provides valuable insights and practical knowledge for attendees seeking deeper understanding and expertise.</p>
					</div>
					<div className="formatted">
						<h5>Showcase Den</h5>
						<p>A dynamic platform for exploring investment opportunities, the Showcase Den highlights key destinations, country spotlights, and new project pitches. Designed to connect delegates with potential investments, this space offers valuable insights into emerging markets and groundbreaking developments in the industry.</p>
					</div>
					<div className="formatted">
						<h5><strong>Pioneer Camp</strong></h5>
						<p>A collaborative space designed for in-depth discussions, the Pioneer Camp caters to the demand for smaller, longer conversations between delegates and experts. Through interactive roundtables, this hub fosters meaningful dialogue, tackles critical challenges, and develops practical solutions for the industry&apos;s future.</p>
					</div>
					<div className="formatted">
						<h5><strong>Boma Talks: </strong></h5>
						<p>The term &ldquo;boma&ldquo; originates from Swahili, meaning an enclosure or fortification, and has evolved to signify a communal gathering place. Serving as the conference&apos;s main stage, Boma Talks will host all keynote presentations and pivotal panel discussions, providing a central platform for sharing insights and fostering dialogue among attendees.</p>
					</div>
				</div>
                
				{/* FILTERS */}
				<div className="container agendaFilters">
                    {/* filters */}
					<div className={CSS.filterLayout}>

                        {/* topics */}
						{
							groupFieldGroupsByName( sessions, 'Topics').length ?
								<section className={CSS.filterLayoutButtons}>
									{
										groupFieldGroupsByName( sessions, 'Topics').map( (loc, index) => (
											<button
												key={index}
												className={`${CSS.filterButton} ${ selectedTopic === loc.id ? CSS.active : null }`} 
												onClick={ () => {
													setSelectedTopic(loc.id)
												}
											}
											>{loc.name}</button>
										))
									}
									<button 
										className={`${CSS.filterButton} ${ selectedTopic === null ? CSS.active : null }`} 
										onClick={ () => {
											setSelectedTopic(null)
										}}
									>All</button>
								</section>
							: null
						}

                        {/* days */}
                        <section className={CSS.filterLayoutButtons}>
                            {
                                buildAgenda( sessions ).map( (day, index) => {
                                    return (
                                        <button 
                                            className={`
                                                ${CSS.filterButton} 
                                                ${ selectedDay === day.time.slice(0, 10) ? CSS.active : null }
                                            `}
                                            key={index} 
                                            onClick={ () => {
                                                setSelectedDay(day.time.slice(0, 10))
                                            }}
                                        >Day { index + 1 }</button>
                                    )
                                })
                            }
                            <button 
                                className={`${CSS.filterButton} ${ selectedDay === null ? CSS.active : null }`} 
                                onClick={ () => {
                                    setSelectedDay(null)
                                }}
                            >All</button>
                        </section>

                        {/* locations */}
                        <section className={CSS.filterLayoutButtons}>
                            <select
								ref={locationSelect}
                                className={CSS.filterSelect} 
                                onChange={ (e) => {
                                    setSelectedLocation( e.target.value )
                                }}
                            >
                                <option value={null}>All locations</option>
                                {
                                    groupFieldGroupsByName( sessions, 'Location').map( filter => {
                                        return (
                                            <option key={filter.id} value={filter.id}>
                                                {filter.name}
                                            </option>
                                        )
                                    })
                                }

                            </select>
                        </section> 

						{/* reset filters */}
						{
							selectedDay || selectedLocation || selectedTopic ?
								<section className={CSS.agendaFilterResetWrapper}>
									<button className={CSS.agendaFilterReset} onClick={resetFilters}>Reset all filters <span>&times;</span></button>
								</section>
							: null
						}
  
                    </div>

					{/* actions */}
					<div className="actions printButton">
						
						<Link href={'/agenda-at-a-glance'}><a className={'button fs-6'}>Agenda at a glance</a></Link>
						<button 
							className={'button fs-6'}
							onClick={ ()=> window.print() }
						><PrinterIcon />&ensp;Print Agenda</button>
					</div>
                </div>


				{/* <p className="my-sm">selectedTopic: { selectedTopic ? selectedTopic : 'x' } | selectedDay: { selectedDay ? selectedDay : 'x' } |  selectedLocation: { selectedLocation }</p> */}


				{/* AGENDA */}
				<div className="agenda | section columns-1 gap-sm mt-md">				
					{
						buildAgenda( sessions ).map( (day, index) => {
							
							if ( selectedDay && selectedDay !== day.time ) return

							return (
								<section key={index} className='day'>
									<p key={index} className='dayDivider'> { day.day }</p>
									{
										day.timeslots.length ?
										day.timeslots.map( (slot, index) => {

											// console.log('slot.sessions:', slot.sessions)
											
											// TARGET SESSIONS BY IDS

											let slotHeader = slot.sessions.find(session => session.id === 'UGxhbm5pbmdfMTc4MzM5Nw==')
											let slotFooter = slot.sessions.find(session => session.id === 'UGxhbm5pbmdfMTk5NDY5OA==')
												
											if (slotHeader || slotFooter) {
													
												const slotHeaderIndex = slot.sessions.findIndex(session => session.id === 'UGxhbm5pbmdfMTc4MzM5Nw==')
												slot.sessions.splice(slotHeaderIndex, 1)
	
												const slotFooterIndex = slot.sessions.findIndex(session => session.id === 'UGxhbm5pbmdfMTk5NDY5OA==')
												slot.sessions.splice(slotFooterIndex, 1)
													
											}
											
											
											return (
												<Fragment key={index}>
													
													{/* TIMESLOT */}

													{/* INJECTED CUSTOM CONTENT */}

													{
														slotHeader ?
															<section className="sessionsWrapper">
																<div className="sessionHeader">
																	<div className="c-white fw-500">
																		{format( new Date(slotHeader.beginsAt), "HH:mm" )} - { format( new Date(slotHeader.endsAt), "HH:mm" )}
																		&ensp;<StopwatchIcon fill={'var(--primary)'}/>&nbsp;{ convertSeconds(parseInt(new Date(slotHeader.endsAt).getTime() - new Date(slotHeader.beginsAt).getTime())/1000) }
																	</div>
																</div>
																<div className='sessionContent | bg-medium columns-1 gap-xs'>
																	<p className="sessionTitle | fs-4 fw-600 lh-1 c-white">{ parse(breakTitle(":", slotHeader.titleTranslations[0].value, ": ")) }</p>
																	<div className="maxw-50">{parse( slotHeader.descriptionTranslations[0].value )}</div>
																</div>
															</section>
														: null
													}
													
													{/* TIMESLOT SESSIONS */}

													<div key={index} className='timeSlot-disabled'>

														{/* SESSIONS */}
														
														<div className="sessions">
															{
																slot.sessions.map( (session, index) => {

																	console.log(session.titleTranslations[0].value)

																	let sessionTheme = getSessionLocationTheme(session)

																	return (
																		<div key={index} className="sessionsWrapper">

																			<div className="sessionHeader">
																				{
																					session.beginsAt && session.endsAt ?
																						<div className="c-white fw-500">
																							{format( new Date(session.beginsAt), "HH:mm" )} - { format( new Date(session.endsAt), "HH:mm" )}
																							&ensp;<StopwatchIcon fill={'var(--primary)'}/>&nbsp;{ convertSeconds(parseInt(new Date(session.endsAt).getTime() - new Date(session.beginsAt).getTime())/1000) }
																						</div>
																					: null
																				}

																				{
																					getSessionFields( session, 'Location').length ?
																						<div className="sessionTagGrid c-white">
																							{session.fields.length ? <LocationIcon fill={'var(--primary)'} /> : null }
																							{getSessionLocation( session )}
																						</div>
																					: null
																				}
																			</div>

																			<div className={`${setSessionTheme(session)} sessionContent | columns-1 gap-xs`} >
																				{/* <p>{session.id}</p> */}
																				<p className="sessionTitle | fs-4 fw-600 lh-1 c-secondary">
																					{ parse(breakTitle(":", session.titleTranslations[0].value, ": ")) }
																				</p>

																				{
																					session.descriptionTranslations.length ?
																					<div className="maxw-45">
																						{parse( session.descriptionTranslations[0].value )}
																					</div>
																					: null
																				}
																				
																				{ sortSpeakers(session.speakers) }
																			</div>
																		</div>
																	)
																})
															}
														</div>

													</div>

													{/* INJECTED CUSTOM CONTENT */}

													{
														slotFooter ?
															<section className="sessionsWrapper">
																<div className="sessionHeader">
																	<div className="c-white fw-500">
																		{format( new Date(slotFooter.beginsAt), "HH:mm" )} - { format( new Date(slotFooter.endsAt), "HH:mm" )}
																		&ensp;<StopwatchIcon fill={'var(--primary)'}/>&nbsp;{ convertSeconds(parseInt(new Date(slotFooter.endsAt).getTime() - new Date(slotFooter.beginsAt).getTime())/1000) }
																	</div>
																</div>
																<div className='sessionContent | bg-medium columns-1 gap-xs'>
																	<p className="sessionTitle | fs-4 fw-600 lh-1 c-white">{ parse(breakTitle(":", slotFooter.titleTranslations[0].value, ": ")) }</p>
																	<div className="maxw-50">{parse( slotFooter.descriptionTranslations[0].value )}</div>

																	{ sortSpeakers(slotFooter.speakers) }
																</div>
															</section>
														: null
													}
													
												</Fragment>
											)
										}) : <p className="fs-5 fw-400 py-xs">No { getFieldGroupNameById( sessions, selectedTopic ) } sessions available { selectedLocation  ? 'at this location' : null } on this day.</p>
									}
								</section>
							)
						})
					}

				</div>


            </section>


		<LenisScroller />
        </div>
    )
}


const SpeakerCard = ({ speaker, type }) => {

	return (
		<div key={speaker.id} className='sessionSpeaker'>
			{ type ? <p className="c-secondary fw-500 pb-xs">{ type }</p> : null }
			{
				speaker.photoUrl ?
					<img src={speaker.photoUrl} alt="" style={{ maxWidth: 100, maxHeight: 100, borderRadius: 100, marginBottom: 10 }} />
				: <img src={`/assets/ui/avatar-fallback.png`} alt="" style={{ maxWidth: 100, maxHeight: 100, borderRadius: 100, marginBottom: 10 }} />

			}
			<p className="fw-600">{ speaker.firstName } { speaker.lastName }</p>
			<p className="fs-sm lh-1">{ speaker.jobTitle }<br />{ speaker.organization }</p>
		</div>
	)
}

const PrinterIcon = ({ color }) => {
	return (
		<svg width="16" height="16" viewBox="0 0 16 18" style={{ fillRule:`evenodd`, clipRule:`evenodd`, strokeMiterlimit:1.5 }}>
			<g transform="matrix(1,0,0,1,-2615,-138)">
				<g id="printer" transform="matrix(0.557918,0,0,0.557918,1290.56,6.44085)">
					<path d="M2381.63,259.726L2375.8,259.726L2375.8,244.224L2400.43,244.224L2400.43,259.726L2394.6,259.726L2394.6,253.717L2381.63,253.717L2381.63,259.726Z" style={{ fill:`none`, stroke: color ? color : 'white', strokeWidth:1.79 }}/>
					<g transform="matrix(0.526495,0,0,0.745724,1130.79,71.5936)">
						<rect x="2375.8" y="244.224" width="24.631" height="15.502" style={{ fill:`none`, stroke: color ? color : 'white', strokeWidth:2.78 }}/>
					</g>
					<g transform="matrix(0.526495,0,0,0.327579,1130.79,159.143)">
						<rect x="2375.8" y="244.224" width="24.631" height="15.502" style={{ fill:`none`, stroke: color ? color : 'white', strokeWidth:4.09 }}/>
					</g>
					<circle cx="2381.62" cy="248.903" r="0.906" style={{ fill: color ? color : 'white' }}/>
					<g transform="matrix(1,0,0,1,2.95813,0)">
						<circle cx="2381.62" cy="248.903" r="0.906" style={{ fill: color ? color : 'white' }}/>
					</g>
				</g>
			</g>
		</svg>
	)
}




export async function getStaticProps() {

	// swapcard API

    const result = await fetch(
		"https://developer.swapcard.com/event-admin/graphql",
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Authorization": process.env.SWAPCARD_AUTH_KEY
			},
			body: JSON.stringify({
				query: `
					query plannings($eventId: String!, $page: Int!) {
						plannings(eventId: $eventId, page: $page, pageSize: 100) {
							id
							titleTranslations {
								value
								language
							}
							beginsAt
							endsAt
							descriptionTranslations {
								value
								language
							}
							format
							fields {
								... on SelectField {
									id
									definition {
										isDefault
										name
									}
									value
									translations {
										value
									}
								}
								... on MultipleSelectField {
									id
									definition {
										isDefault
										name
									}
									value
									translations {
										value
									}
								}
							}
							speakers {
								firstName
								id
								jobTitle
								lastName
								photoUrl
								organization
								withEvent(eventId: "${process.env.SWAPCARD_EVENT_ID_2025}") {
									eventProfile {
										type
									}
								}
							}
							categories
						}
					}
				`,
				variables: {
					"eventId": process.env.SWAPCARD_EVENT_ID_2025,
					"page": 1
				}
			})
		}
	)

	// console.log(result)

	const agenda = await result.json()



	// cms (theme etc)
    const cmsAgendaData = await craftApolloClient().query({
        query: gql`
			query SEO {
				entry(site: "${process.env.SITE_HANDLE}", id: "	16596") {
					title
                    ... on ${process.env.SITE_HANDLE}Agenda_${process.env.SITE_HANDLE}Agenda_Entry {
                        id
                        agendaTheme {
                            ... on agendaTheme_BlockType {
                                id
                                color
                                agendaTag
                            }
                        }
                    }
					seomatic (asArray: true) {
						metaTitleContainer
						metaTagContainer
					}
				}
			}
        `
    })


    return { 
        props: { 
            sessions: agenda.data.plannings,
			theme: cmsAgendaData.data.entry.agendaTheme
        } 
    }
}

